import { useState } from "react";
import { motion } from "framer-motion";

const ease = [0.16, 1, 0.3, 1];

const LAYERS = [
  {
    id: 0,
    num: "01",
    serum: "White Serum",
    layer: "Top Layer",
    title: "Barrier Repair & Ageing Defense",
    desc: "Seals and strengthens the outermost barrier to lock in moisture and shield against the daily stressors that accelerate visible ageing.",
    color: "#ECEAF6",
    swatchBorder: "rgba(20,15,44,0.18)",
    glow: "rgba(27,23,53,0.55)",
    dark: false,
  },
  {
    id: 1,
    num: "02",
    serum: "Pink Serum",
    layer: "Mid Layer",
    title: "Cell Regeneration & Firmness",
    desc: "Activates renewal, rebuilding bounce, elasticity and firmness for visibly plumper, resilient skin.",
    color: "#EFA6C8",
    swatchBorder: "transparent",
    glow: "rgba(239,166,200,0.5)",
    dark: false,
  },
  {
    id: 2,
    num: "03",
    serum: "Yellow Serum",
    layer: "Deeper Layer",
    title: "Dark Spots & Radiance",
    desc: "Travels to the deeper layers to target multiple-pathways of pigmentation to fade dark spots and even tone, unveiling a lasting, lit-from-within radiance.",
    color: "#EFC65B",
    swatchBorder: "transparent",
    glow: "rgba(239,198,91,0.5)",
    dark: false,
  },
];

const HOTSPOTS = [
  { id: 0, top: "51.5%", left: "51%" }, // White serum - window centre
  { id: 1, top: "51.5%", left: "55%" }, // Pink serum - window right
  { id: 2, top: "51.5%", left: "47%" }, // Yellow serum - window left
];

const CARD_BG =
  "radial-gradient(120% 100% at 50% 30%, #ffffff 0%, #f3f0ea 100%)";

export default function TriDelivery() {
  const [active, setActive] = useState(0);

  return (
    <section
      className="relative w-full px-6 md:px-16 py-14 md:py-24 stage-bg"
      data-testid="tridelivery-section"
    >
      <div className="mx-auto max-w-7xl">
        <div className="frame flex items-center justify-between px-5 py-3 mb-10">
          <span className="text-[0.58rem] track-sm uppercase" style={{ color: "var(--paper-dim)" }}>
            01 / Technology
          </span>
          <span className="text-[0.58rem] track-sm uppercase font-bold" style={{ color: "var(--paper-dim)" }}>
            Tri-Delivery&trade; Technology
          </span>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease }}
          className="max-w-3xl"
        >
          <span
            className="text-[0.62rem] track-sm uppercase"
            style={{ color: "var(--paper-dim)" }}
          >
            Powered By <span className="font-bold" style={{ color: "var(--paper)" }}>Tri-Delivery&trade; Technology</span>
          </span>
          <h2 className="headline text-3xl sm:text-4xl lg:text-5xl mt-5">
            Three serums.
            <br />
            Three skin layers.
          </h2>
          <p
            className="mt-6 text-base leading-relaxed max-w-xl"
            style={{ color: "var(--paper-dim)" }}
          >
            Our Patent-pending <strong style={{ color: "var(--paper)" }}>Tri-Delivery&trade; Technology</strong> targets skin
            ageing across 3 different layers of the skin.
          </p>
        </motion.div>

        <div className="mt-12 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          {/* LEFT: bottle with interactive hotspots */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="relative"
          >
            <div
              className="relative w-full max-w-[440px] mx-auto aspect-[4/5] rounded-sm overflow-hidden"
              style={{ background: CARD_BG, border: "1px solid rgba(27,23,53,0.12)" }}
            >
              <picture>
                <source
                  media="(max-width: 767px)"
                  srcSet="/serum-3circles-m.webp"
                  type="image/webp"
                />
                <img
                  src="/serum-3circles.webp"
                  alt="Open Evove triple-chamber serum bottle"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-contain p-8"
                />
              </picture>

              {HOTSPOTS.map((h) => {
                const L = LAYERS[h.id];
                const isActive = active === h.id;
                return (
                  <button
                    key={h.id}
                    onMouseEnter={() => setActive(h.id)}
                    onClick={() => setActive(h.id)}
                    data-testid={`tridelivery-hotspot-${h.id}`}
                    aria-label={L.title}
                    className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full outline-none"
                    style={{ top: h.top, left: h.left }}
                  >
                    <span
                      className="block rounded-full transition-all duration-500"
                      style={{
                        width: isActive ? 20 : 13,
                        height: isActive ? 20 : 13,
                        background: L.color,
                        border: `1px solid ${L.swatchBorder}`,
                        boxShadow: isActive
                          ? `0 0 0 6px ${L.glow}, 0 0 26px ${L.glow}`
                          : `0 0 0 3px rgba(255,255,255,0.55)`,
                      }}
                    />
                    {!isActive && (
                      <span
                        className="absolute inset-0 m-auto rounded-full animate-ping"
                        style={{
                          width: 13,
                          height: 13,
                          background: L.color,
                          opacity: 0.55,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* RIGHT: skin-strata chapters */}
          <div className="relative">
            {/* depth axis */}
            <div className="absolute -left-6 top-2 bottom-2 hidden md:flex flex-col justify-between items-center">
              <span
                className="text-[0.5rem] track-sm uppercase [writing-mode:vertical-rl] rotate-180"
                style={{ color: "var(--paper-faint)" }}
              >
                Surface
              </span>
              <div
                className="flex-1 my-3 w-px"
                style={{
                  background:
                    "linear-gradient(to bottom, rgba(27,23,53,0.35), rgba(27,23,53,0.06))",
                }}
              />
              <span
                className="text-[0.5rem] track-sm uppercase [writing-mode:vertical-rl] rotate-180"
                style={{ color: "var(--paper-faint)" }}
              >
                Deep
              </span>
            </div>

            <div className="flex flex-col gap-3">
              {LAYERS.map((L, i) => {
                const isActive = active === i;
                return (
                  <motion.button
                    key={L.id}
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onClick={() => setActive(i)}
                    onViewportEnter={() => setActive(i)}
                    viewport={{ amount: 0.8 }}
                    data-testid={`tridelivery-layer-${i}`}
                    className="relative text-left rounded-sm overflow-hidden transition-all duration-500 w-full"
                    style={{
                      border: "1px solid rgba(27,23,53,0.14)",
                      background: isActive
                        ? `linear-gradient(100deg, ${L.color}22 0%, rgba(20,15,44,0) 60%)`
                        : "transparent",
                      boxShadow: isActive ? `inset 3px 0 0 0 ${L.color}` : "inset 3px 0 0 0 transparent",
                    }}
                  >
                    <div className="flex items-start gap-5 p-6 md:p-7">
                      <span
                        className="text-[0.7rem] track-sm mt-1"
                        style={{ color: isActive ? "var(--paper)" : "var(--paper-faint)" }}
                      >
                        {L.num}
                      </span>

                      <span
                        className="mt-1 shrink-0 rounded-full transition-all duration-500"
                        style={{
                          width: 16,
                          height: 16,
                          background: L.color,
                          border: `1px solid ${L.swatchBorder}`,
                          boxShadow: isActive ? `0 0 16px ${L.glow}` : "none",
                        }}
                      />

                      <div className="flex-1">
                        <div
                          className="text-[0.6rem] track-sm uppercase"
                          style={{ color: "var(--paper-faint)" }}
                        >
                          {L.serum} &middot; {L.layer}
                        </div>
                        <div
                          className="mt-2 text-[1.05rem] md:text-[1.18rem] font-medium tracking-tight"
                          style={{ color: "var(--paper)" }}
                        >
                          {L.title}
                        </div>

                        <motion.div
                          initial={false}
                          animate={{
                            height: isActive ? "auto" : 0,
                            opacity: isActive ? 1 : 0,
                          }}
                          transition={{ duration: 0.5, ease }}
                          className="overflow-hidden"
                        >
                          <p
                            className="pt-3 text-[0.92rem] leading-relaxed max-w-md"
                            style={{ color: "var(--paper-dim)" }}
                          >
                            {L.desc}
                          </p>
                        </motion.div>
                      </div>
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
